import { Person } from "@/modules/common/entities/person";
import Table, {
  TableProps,
  useOptionsColumn,
} from "@/shared/data-display/table";
import Text from "@/shared/typography/text";
import { useDocumentsTranslations } from "@/modules/documents/shared/utils/use-documents-translations";
import { FC, useCallback } from "react";
import { twMerge } from "tailwind-merge";
import { CompanyDocumentAnalyticsOptionsProps } from "@/modules/documents/widgets/company-document-analytics-options";
import {
  CompanyDocumentAnalyticsRecordFragment,
  CompanyDocumentReadConfirmationType,
} from "gql/graphql";
import { getParticipantStatus } from "../model/get-participant-status";
import { ParticipantStatusLabel } from "./participant-status";
import { CompanyDocumentReadConfirmation } from "@/modules/documents/entities/company-document-read-confirmation";

export function CompanyDocumentAnalyticsView({
  Options,
  readConfirmationType,
  selectedId,
  onSelect,
  ...props
}: TableProps<CompanyDocumentAnalyticsRecordFragment> & {
  Options: FC<CompanyDocumentAnalyticsOptionsProps>;
  readConfirmationType?: CompanyDocumentReadConfirmationType;
  selectedId?: string;
  onSelect?: (record: CompanyDocumentAnalyticsRecordFragment) => void;
}) {
  const t = useDocumentsTranslations();

  const OptionsCell = useCallback(
    ({ record }: { record: CompanyDocumentAnalyticsRecordFragment }) => (
      <div onClick={(e) => e.stopPropagation()}>
        <Options ids={record.id} />
      </div>
    ),
    [Options],
  );

  const options = useOptionsColumn<CompanyDocumentAnalyticsRecordFragment>({
    Options: OptionsCell,
  });

  return (
    <Table
      {...props}
      rowKey={(record) => record.id}
      onRow={(record) => ({
        className: twMerge(
          onSelect && "cursor-pointer",
          selectedId === record.id && "!bg-[--palette-primary-1]",
        ),
        onClick: () => onSelect?.(record),
      })}
      columns={[
        {
          key: "employee",
          title: t("Employee / position"),
          render: (_, { person }) => (
            <Person.NameColumn
              data={person}
              extra={
                <div className={"scale-90 origin-top-left"}>
                  <Person.PositionAndOrganization data={person} />
                </div>
              }
            />
          ),
        },
        {
          key: "status",
          title: t("Status"),
          render: (_, record) => (
            <div className="flex flex-col">
              <ParticipantStatusLabel
                status={getParticipantStatus(record, readConfirmationType)}
              />
              {record.confirmDate && (
                <Text type="caption" color="secondary">
                  <CompanyDocumentReadConfirmation.ConfirmDate data={record} />
                </Text>
              )}
            </div>
          ),
        },
        options,
      ]}
    />
  );
}
